import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, TextButton } from '@wix/wix-base-ui';
import { cx, hoc } from '#packages/util';
import { withRestrictions } from '#packages/editorRestrictions';
import { AUTOMATION_IDS } from '../constants';
import { mapStateToProps } from './SectionEmptyState.mapper';
import styles from './SectionsEmptyState.scss';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import type { SectionEmptyStateStateProps } from './SectionEmptyState.mapper';

export interface SectionEmptyStateOwnProps {
  onAddElementsClick: React.MouseEventHandler;
  onAddSectionClick: React.MouseEventHandler;
  onAddSectionWithAiClick: React.MouseEventHandler;
}

interface SectionEmptyStateProps
  extends SectionEmptyStateOwnProps,
    SectionEmptyStateStateProps {}

const SectionEmptyStateComponent: React.FC<SectionEmptyStateProps> = ({
  onAddSectionClick,
  onAddSectionWithAiClick,
  onAddElementsClick,
  isLightTextColor,
  isEmptyStateAddPanelVisible,
  shouldDisplayAISectionGeneratorButton,
  isEmptyStateTitleTextVisible,
}) => {
  const { t } = useTranslation();
  const renderLinks = () => {
    if (isEmptyStateAddPanelVisible) {
      const emptyStateButtonsDetails = [
        {
          onClick: onAddSectionClick,
          dataHook: AUTOMATION_IDS.EMPTY_STATE.ADD_SECTION,
          className: styles.designedSectionIcon,
          name: 'emptyStateDesignedSection.',
          text: t('section_add_empty_state_designed_label'),
          key: 'emptyStateDesignedSection.',
          isVisible: true,
        },
        {
          onClick: onAddSectionWithAiClick,
          dataHook: AUTOMATION_IDS.EMPTY_STATE.ADD_SECTION_AI,
          className: styles.generateWithAiIcon,
          name: 'emptyStateAISection',
          text: t('section_add_empty_state_AI_label'),
          key: 'emptyStateAISection',
          isVisible: shouldDisplayAISectionGeneratorButton,
        },
        {
          onClick: onAddElementsClick,
          dataHook: AUTOMATION_IDS.EMPTY_STATE.ADD_ELEMENT,
          className: styles.addElementIcon,
          name: 'emptyStateAddElement',
          text: t('section_add_empty_state_elements_label'),
          key: 'emptyStateAddElement',
          isVisible: true,
        },
      ];
      const visibleEmptyStateButtonsDetails = emptyStateButtonsDetails.filter(
        (buttonDetails) => buttonDetails.isVisible,
      );
      // hide ai section generator for schoolMode
      return visibleEmptyStateButtonsDetails.map((buttonDetails) => {
        return (
          <div
            className={styles.linkButton}
            onClick={buttonDetails.onClick}
            data-hook={buttonDetails.dataHook}
            key={buttonDetails.key}
          >
            <Symbol
              className={cx(styles.linkButtonSymbol, buttonDetails.className)}
              name={buttonDetails.name}
            />
            <span>{buttonDetails.text}</span>
          </div>
        );
      });
    }

    return (
      <TextButton
        size="small"
        skin="standard"
        weight="normal"
        dataHook={AUTOMATION_IDS.EMPTY_STATE.ADD_SECTION}
        onClick={onAddSectionClick}
      >
        lite_editor_blank_section_empty_state_text
      </TextButton>
    );
  };

  return (
    <div
      data-hook={AUTOMATION_IDS.EMPTY_STATE.CONTAINER}
      className={cx(styles.emptyStateContainer, {
        [styles.light]: isLightTextColor,
      })}
    >
      {isEmptyStateTitleTextVisible && (
        <Text
          size="small"
          skin="secondary"
          weight="normal"
          shouldTranslate={false}
        >
          {t('section_add_empty_state_title')}
        </Text>
      )}
      <div
        className={styles.linksContainer}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {renderLinks()}
      </div>
    </div>
  );
};

export const SectionEmptyState = withRestrictions('sections_empty-state')(
  hoc.connect(
    hoc.STORES.EDITOR_API,
    mapStateToProps,
    null,
  )(SectionEmptyStateComponent),
);
